import { Track, DJTrackDetails, DJTransition, DJPlaylist, DJPlaylistPreferences } from '../models';
import { openaiService } from './openaiService';

/**
 * Service for analyzing tracks and creating DJ playlists using the Sonar model
 */
export class SonarService {
  /**
   * Analyze a track to extract detailed musical information
   * @param track The track to analyze
   * @returns Detailed track analysis
   */
  public async analyzeTrack(track: Track): Promise<DJTrackDetails> {
    try {
      const messages = [
        {
          role: 'system' as const,
          content: 'You are Sonar, a music analysis AI that specializes in analyzing tracks and providing detailed musical information.'
        },
        {
          role: 'user' as const,
          content: `
            Analyze the following track and provide detailed musical information:
            
            Track: "${track.name}"
            Artist(s): ${track.artists.map(a => a.name).join(', ')}
            Album: "${track.album.name}"
            
            Please provide the following information:
            - Estimated BPM (tempo)
            - Musical key
            - Energy level (0-1 scale)
            - Danceability (0-1 scale)
            - Loudness (dB)
            - Track sections (intro, verse, chorus, bridge, outro, etc.) with timestamps
            - Recommended fade-in and fade-out points
            - Hot cues (important points in the track)
            
            Format your response as a JSON object.
          `
        }
      ];

      const response = await openaiService.sendFunctionlessMessage(messages, 'gpt-4.1-nano');

      // Parse the response
      const content = response.choices[0].message.content || '{}';
      
      // Extract JSON from the response, handling markdown code blocks
      let jsonContent = content;
      
      // Handle markdown code blocks
      if (content.includes('```json')) {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      } else if (content.includes('```')) {
        const jsonMatch = content.match(/```\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      }
      
      // Handle cases where the response starts with explanatory text
      if (!jsonContent.trim().startsWith('{')) {
        const jsonStartIndex = jsonContent.indexOf('{');
        if (jsonStartIndex !== -1) {
          jsonContent = jsonContent.substring(jsonStartIndex);
        }
      }
      
      // Provide a fallback if we can't extract valid JSON
      let analysisData: {
        bpm?: number;
        key?: string;
        energy?: number;
        danceability?: number;
        loudness?: number;
        sections?: Array<{
          start: number;
          duration: number;
          bpm?: number;
          key?: string;
          type: 'intro' | 'verse' | 'chorus' | 'bridge' | 'outro' | 'drop';
          energy?: number;
        }>;
        fadeInDuration?: number;
        fadeOutDuration?: number;
        hotCues?: Array<{
          position: number;
          type: 'intro' | 'drop' | 'breakdown' | 'outro' | 'custom';
          label?: string;
        }>;
      };
      
      try {
        analysisData = JSON.parse(jsonContent);
      } catch (error) {
        console.warn('Failed to parse JSON response, using default values', error);
        analysisData = {
          bpm: 120,
          key: 'C Major',
          energy: 0.5,
          danceability: 0.5,
          loudness: -8,
          sections: [],
          fadeInDuration: 2000,
          fadeOutDuration: 2000,
          hotCues: []
        };
      }
      
      // Map the response to our DJTrackDetails interface
      const trackDetails: DJTrackDetails = {
        bpm: analysisData.bpm || 120,
        key: analysisData.key || 'C Major',
        energy: analysisData.energy || 0.5,
        danceability: analysisData.danceability || 0.5,
        loudness: analysisData.loudness || -8,
        sections: analysisData.sections?.map((section: any) => ({
          start: section.start,
          duration: section.duration,
          bpm: section.bpm || analysisData.bpm,
          key: section.key,
          type: section.type,
          energy: section.energy || 0.5
        })) || [],
        fadeInDuration: analysisData.fadeInDuration,
        fadeOutDuration: analysisData.fadeOutDuration,
        hotCues: analysisData.hotCues?.map((cue: any) => ({
          position: cue.position,
          type: cue.type,
          label: cue.label
        }))
      };

      return trackDetails;
    } catch (error) {
      console.error('Error analyzing track:', error);
      throw new Error('Failed to analyze track');
    }
  }

  /**
   * Generate a transition recommendation between two tracks
   * @param fromTrack The source track
   * @param toTrack The destination track
   * @returns Transition recommendation
   */
  public async recommendTransition(
    fromTrack: Track & { details?: DJTrackDetails }, 
    toTrack: Track & { details?: DJTrackDetails }
  ): Promise<DJTransition> {
    try {
      // If we don't have track details, analyze them first
      const fromDetails = fromTrack.details || await this.analyzeTrack(fromTrack);
      const toDetails = toTrack.details || await this.analyzeTrack(toTrack);
      
      const messages = [
        {
          role: 'system' as const,
          content: 'You are Sonar, a music analysis AI that specializes in DJ transitions between tracks.'
        },
        {
          role: 'user' as const,
          content: `
            Recommend a DJ transition between these two tracks:
            
            FROM TRACK:
            Title: "${fromTrack.name}"
            Artist(s): ${fromTrack.artists.map(a => a.name).join(', ')}
            BPM: ${fromDetails.bpm}
            Key: ${fromDetails.key}
            Energy: ${fromDetails.energy}
            
            TO TRACK:
            Title: "${toTrack.name}"
            Artist(s): ${toTrack.artists.map(a => a.name).join(', ')}
            BPM: ${toDetails.bpm}
            Key: ${toDetails.key}
            Energy: ${toDetails.energy}
            
            Please provide:
            - Best timestamp to start fading out the first track (ms from start)
            - Best timestamp to start fading in the second track (ms from start)
            - Recommended transition duration (ms)
            - Transition type (beatmatch, harmonic, energy, cut)
            - Compatibility score (0-100)
            - Brief notes about this transition
            
            Format your response as a JSON object.
          `
        }
      ];

      const response = await openaiService.sendFunctionlessMessage(messages, 'gpt-4o');

      // Parse the response
      const content = response.choices[0].message.content || '{}';
      
      // Extract JSON from the response, handling markdown code blocks
      let jsonContent = content;
      
      // Handle markdown code blocks
      if (content.includes('```json')) {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      } else if (content.includes('```')) {
        const jsonMatch = content.match(/```\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      }
      
      // Handle cases where the response starts with explanatory text
      if (!jsonContent.trim().startsWith('{')) {
        const jsonStartIndex = jsonContent.indexOf('{');
        if (jsonStartIndex !== -1) {
          jsonContent = jsonContent.substring(jsonStartIndex);
        }
      }
      
      // Provide a fallback if we can't extract valid JSON
      let transitionData: {
        fromTrackTimestamp?: number;
        toTrackTimestamp?: number;
        duration?: number;
        type?: 'beatmatch' | 'harmonic' | 'energy' | 'cut';
        compatibilityScore?: number;
        notes?: string;
      };
      
      try {
        transitionData = JSON.parse(jsonContent);
      } catch (error) {
        console.warn('Failed to parse JSON response for transition, using default values', error);
        transitionData = {
          fromTrackTimestamp: fromTrack.durationMs - 30000,
          toTrackTimestamp: 0,
          duration: 15000,
          type: 'beatmatch',
          compatibilityScore: 70,
          notes: 'Default transition'
        };
      }
      
      // Map the response to our DJTransition interface
      const transition: DJTransition = {
        fromTrackId: fromTrack.id,
        toTrackId: toTrack.id,
        fromTrackTimestamp: transitionData.fromTrackTimestamp || (fromTrack.durationMs - 30000), // Default to 30s before end
        toTrackTimestamp: transitionData.toTrackTimestamp || 0, // Default to start of track
        duration: transitionData.duration || 15000, // Default to 15s transition
        type: transitionData.type || 'beatmatch',
        compatibilityScore: transitionData.compatibilityScore || 70,
        transitionNotes: transitionData.notes
      };

      return transition;
    } catch (error) {
      console.error('Error recommending transition:', error);
      throw new Error('Failed to recommend transition');
    }
  }

  /**
   * Create a complete DJ playlist with transitions
   * @param tracks Pool of tracks to choose from
   * @param preferences User preferences for the playlist
   * @returns Complete DJ playlist with transitions
   */
  public async createDJPlaylist(
    tracks: Track[],
    preferences: DJPlaylistPreferences
  ): Promise<DJPlaylist> {
    try {
      // First, get a list of tracks with their details
      const tracksWithDetails = await Promise.all(
        tracks.slice(0, 20).map(async (track) => {
          const details = await this.analyzeTrack(track);
          return { ...track, details };
        })
      );
      
      // Create messages to generate the optimal track order
      const messages = [
        {
          role: 'system' as const,
          content: 'You are Sonar, a music analysis AI that specializes in creating DJ playlists with optimal track ordering and transitions.'
        },
        {
          role: 'user' as const,
          content: `
            Create a DJ playlist with the following preferences:
            
            Name: "${preferences.name}"
            Description: "${preferences.description}"
            Duration: ${preferences.duration} minutes
            Energy Profile: ${preferences.energyProfile}
            BPM Range: ${preferences.bpmRange[0]}-${preferences.bpmRange[1]} BPM
            Transition Style: ${preferences.transitionStyle}
            ${preferences.genres ? `Genres: ${preferences.genres.join(', ')}` : ''}
            
            Available tracks:
            ${tracksWithDetails.map((track: any, index: number) => `
              ${index + 1}. "${track.name}" by ${track.artists.map((a: any) => a.name).join(', ')}
              - BPM: ${track.details.bpm}
              - Key: ${track.details.key}
              - Energy: ${track.details.energy}
              - Duration: ${Math.floor(track.durationMs / 1000)}s
            `).join('\n')}
            
            Please select and order 5-10 tracks that would create the best DJ mix according to the preferences.
            For each track, include its position in the list above.
            Also provide an overall BPM for the mix and an energy curve (array of values 0-1).
            
            Format your response as a JSON object with:
            - trackIndices: array of track indices from the list above
            - bpm: overall BPM of the mix
            - energyCurve: array of energy values (0-1) representing the energy progression
            - djNotes: brief notes about the mix
          `
        }
      ];

      const response = await openaiService.sendFunctionlessMessage(messages, 'gpt-4o');

      // Parse the response
      const content = response.choices[0].message.content || '{}';
      
      // Extract JSON from the response, handling markdown code blocks
      let jsonContent = content;
      
      // Handle markdown code blocks
      if (content.includes('```json')) {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      } else if (content.includes('```')) {
        const jsonMatch = content.match(/```\s*([\s\S]*?)\s*```/);
        if (jsonMatch && jsonMatch[1]) {
          jsonContent = jsonMatch[1].trim();
        }
      }
      
      // Handle cases where the response starts with explanatory text
      if (!jsonContent.trim().startsWith('{')) {
        const jsonStartIndex = jsonContent.indexOf('{');
        if (jsonStartIndex !== -1) {
          jsonContent = jsonContent.substring(jsonStartIndex);
        }
      }
      
      // Provide a fallback if we can't extract valid JSON
      let playlistData: {
        trackIndices?: number[];
        bpm?: number;
        energyCurve?: number[];
        djNotes?: string;
      };
      
      try {
        playlistData = JSON.parse(jsonContent);
      } catch (error) {
        console.warn('Failed to parse JSON response for playlist, using default values', error);
        playlistData = {
          trackIndices: [1, 2, 3, 4, 5], // Default to first 5 tracks
          bpm: 120,
          energyCurve: [0.5, 0.6, 0.7, 0.8, 0.7],
          djNotes: 'Default DJ mix'
        };
      }
      
      // Get the selected tracks in the recommended order
      const trackIndices = playlistData.trackIndices || [1, 2, 3, 4, 5]; // Default to first 5 tracks if undefined
      const selectedTracks = trackIndices.map(
        (index: number) => tracksWithDetails[index - 1]
      );
      
      // Generate transitions between adjacent tracks
      const transitions: DJTransition[] = [];
      for (let i = 0; i < selectedTracks.length - 1; i++) {
        const transition = await this.recommendTransition(
          selectedTracks[i],
          selectedTracks[i + 1]
        );
        transitions.push(transition);
      }
      
      // Calculate estimated duration
      const estimatedDuration = selectedTracks.reduce(
        (total: number, track: Track) => total + track.durationMs,
        0
      );
      
      // Create the DJ playlist
      const djPlaylist: DJPlaylist = {
        id: `dj-${Date.now()}`,
        name: preferences.name,
        description: preferences.description,
        owner: {
          id: 'user',
          displayName: 'You'
        },
        tracks: selectedTracks.map((track: Track) => ({
          id: track.id,
          name: track.name,
          artists: track.artists,
          album: track.album,
          uri: track.uri,
          durationMs: track.durationMs,
          explicit: track.explicit,
          popularity: track.popularity,
          previewUrl: track.previewUrl,
          imageUrl: track.imageUrl
        })),
        transitions,
        bpm: playlistData.bpm || 120,
        energyCurve: playlistData.energyCurve || Array(selectedTracks.length).fill(0.5),
        estimatedDuration,
        djNotes: playlistData.djNotes,
        uri: '',
        totalTracks: selectedTracks.length
      };

      return djPlaylist;
    } catch (error) {
      console.error('Error creating DJ playlist:', error);
      throw new Error('Failed to create DJ playlist');
    }
  }
}

// Create a singleton instance
export const sonarService = new SonarService();
